import RevolutCheckout, {
  RevolutCheckoutCardField,
  RevolutPaymentsModuleInstance,
} from '@revolut/checkout';
import { useMutation, useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useRouter } from 'next/router';
import * as Sentry from '@sentry/nextjs';
import {
  useCallback,
  useEffect,
  useState,
  useRef,
  type Dispatch,
  type SetStateAction,
} from 'react';
import { deleteCart, getCart } from '@apiCalls';
import { CtaContainer, InputField } from '@components';
import {
  ESCROW_EXPIRY_MINUTES,
  FRONTEND_BASE_URL,
  PAYMENT_SUBMITTED_AT_KEY,
} from '@constants';
import { type ITicketState, useAppContext } from '@contexts';
import { currencyLookup, formatValue } from '@utils';
import {
  withErrorHandler,
  withErrorBoundary,
  handleError,
} from '@utils/errorHandler';
import tracker from '@utils/tracking';

type RevolutCardFormProps = {
  clientSecret: string;
  publicKey: string;
  cartId: string;
  event: IEvent;
  eventSeries: IEventSeries | undefined;
  tickets: ITicketState['tickets'];
  promoCode?: string;
  billingDetails?: IBillingDetails;
  setEscrowExpiryTime: Dispatch<SetStateAction<Date | undefined>>;
};

type RevolutMountRef = HTMLDivElement | null;

const locale = 'en';

const separatorStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  color: 'rgba(var(--primary-colour), 1)',
  fontSize: '14px',
};

const lineStyle = {
  flex: 1,
  height: '1px',
  backgroundColor: 'rgba(var(--primary-colour), 1)',
};

export function RevolutCardForm({
  billingDetails,
  cartId,
  clientSecret,
  event,
  eventSeries,
  promoCode,
  publicKey,
  setEscrowExpiryTime,
  tickets,
}: RevolutCardFormProps) {
  const { ticketDetails, escrowExpiryTime, setPaymentCompleted } =
    useAppContext();

  /**
   * Record that money has been committed, before the cart is converted. This
   * stops the escrow timer evicting the buyer mid-confirmation, and lets the
   * confirmation step tell how long the payment has had to settle.
   */
  const markPaymentSubmitted = () => {
    setPaymentCompleted(true);

    try {
      localStorage.setItem(PAYMENT_SUBMITTED_AT_KEY, new Date().toISOString());
    } catch {
      // A blocked localStorage must not stop the purchase completing.
    }
  };
  const [cardholderName, setCardholderName] = useState('');
  const [blocked, setBlocked] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [didFail, setDidFail] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [isCardValid, setIsCardValid] = useState(false);
  const deleteCartMutation = useMutation({ mutationFn: deleteCart });
  const router = useRouter();
  const cardFieldMountRef = useRef<RevolutMountRef>(null);
  const cardFieldInstanceRef = useRef<RevolutCheckoutCardField | null>(null);
  const AppleGooglePayMountRef = useRef<RevolutMountRef>(null);
  const AppleGooglePayInstanceRef = useRef<{
    render: () => Promise<void>;
    canMakePayment: () => Promise<'applePay' | 'googlePay' | null>;
    destroy: () => void;
  } | null>(null);
  const RevolutPayMountRef = useRef<RevolutMountRef>(null);
  const RevolutPayInstanceRef = useRef<
    RevolutPaymentsModuleInstance['revolutPay'] | null
  >(null);
  /**
   * Mount guards. These must be refs, not state: each initializer awaits the
   * Revolut SDK before it can mount, and a state flag set after that await is
   * still false to any effect re-run in the meantime, which mounts a second
   * button into the same node. A ref claimed before the await closes that gap.
   */
  const initAppleGooglePayRef = useRef(false);
  const initRevolutPayRef = useRef(false);
  const initRevolutCardFieldRef = useRef(false);
  const purchaseTrackedRef = useRef(false);
  const { data: cartData } = useQuery({
    queryKey: ['cart', cartId],
    queryFn: () => getCart(cartId),
    enabled: !!cartId,
  });

  const currency = tickets ? tickets[0]?.Currency : 'EUR';
  const totalAmount = cartData?.TotalAmount;
  const environment =
    process.env.NEXT_PUBLIC_APP_ENV === 'production' ? 'prod' : 'sandbox';
  const publicToken = publicKey;
  const handleSubmit = useCallback(
    (uiEvent: React.MouseEvent) => {
      uiEvent.preventDefault();

      if (!cardFieldInstanceRef.current) {
        const error = new Error('Card field instance is not ready.');
        handleError(error, {
          inAsync: true,
          tags: {
            operation: 'handleSubmit',
            component: 'RevolutCardForm',
          },
        });
        return;
      }
      if (!cardholderName) return setError('Incomplete billing details');

      setLoading(true);

      tracker.track({
        event: {
          event: 'Payment Attempted',
          category: 'Payment',
          value: eventSeries?.ID ?? event.ID,
        },
        platforms: ['ga'],
      });

      try {
        cardFieldInstanceRef.current.submit({
          name: cardholderName,
          email: billingDetails?.Email,
          phone: billingDetails?.Phone,
        });
      } catch (error) {
        reset();
      }
    },
    [cardholderName]
  );

  const reset = useCallback(() => {
    withErrorHandler(() => {
      // Destroy all SDK instances before resetting
      if (cardFieldInstanceRef.current) {
        try {
          cardFieldInstanceRef.current.destroy();
          cardFieldInstanceRef.current = null;
        } catch (e) {
          handleError(e, {
            inAsync: true,
            tags: {
              operation: 'destroyCardFieldInstance',
              component: 'RevolutCardForm',
            },
          });
        }
      }
      if (AppleGooglePayInstanceRef.current) {
        try {
          AppleGooglePayInstanceRef.current.destroy();
          AppleGooglePayInstanceRef.current = null;
        } catch (e) {
          handleError(e, {
            inAsync: true,
            tags: {
              operation: 'destroyAppleGooglePayInstance',
              component: 'RevolutCardForm',
            },
          });
        }
      }
      if (RevolutPayInstanceRef.current) {
        try {
          RevolutPayInstanceRef.current.destroy();
          RevolutPayInstanceRef.current = null;
        } catch (e) {
          handleError(e, {
            inAsync: true,
            tags: {
              operation: 'destroyRevolutPayInstance',
              component: 'RevolutCardForm',
            },
          });
        }
      }

      // Release the mount guards so a retry can remount
      initAppleGooglePayRef.current = false;
      initRevolutPayRef.current = false;
      initRevolutCardFieldRef.current = false;

      if (cartId) deleteCartMutation.mutate(cartId);
      setEscrowExpiryTime(
        dayjs().add(ESCROW_EXPIRY_MINUTES, 'minutes').toDate()
      );
      if (tickets) {
        setTimeout(() => {
          router.replace(`/event/${event.ID}/checkout`);
        }, 5000);
      }
    });
    setDidFail(true);
    setLoading(false);
    setBlocked(false);
  }, []);

  const trackSuccessfulPurchase = useCallback(() => {
    if (purchaseTrackedRef.current) return;
    if (!cartData || !tickets || !tickets.length || !event) return;

    purchaseTrackedRef.current = true;
    tracker.trackPurchase(cartData, tickets, event, currency);
  }, [cartData, tickets, event, currency]);

  const initializeApplePayGooglePay = useCallback(async () => {
    if (
      !AppleGooglePayMountRef.current ||
      totalAmount === null ||
      totalAmount === undefined ||
      initAppleGooglePayRef.current
    ) {
      return;
    }

    // Claim the mount before awaiting, so a concurrent call cannot also mount.
    initAppleGooglePayRef.current = true;

    try {
      const paymentsInstance = await RevolutCheckout.payments({
        locale,
        publicToken,
        mode: environment,
      });

      // Check if component is still mounted and ref is still valid
      if (!AppleGooglePayMountRef.current) {
        initAppleGooglePayRef.current = false;
        return;
      }

      const paymentRequest = paymentsInstance.paymentRequest;
      const paymentRequestInstance = paymentRequest(
        AppleGooglePayMountRef.current as HTMLElement,
        {
          createOrder: () =>
            new Promise<{ publicId: string }>(resolve =>
              resolve({
                publicId: clientSecret,
              })
            ),
          currency,
          amount: totalAmount,
          onSuccess: () => {
            markPaymentSubmitted();
            trackSuccessfulPurchase();
            router.push(
              `/event/${event.ID || eventSeries?.ID}/checkout/contact`
            );
          },
          onError: error => {
            tracker.track({
              event: {
                event: 'Payment Failed',
                category: 'Payment',
                label: `Amount: ${currencyLookup(currency)}${formatValue(
                  totalAmount
                )}`,
                value: eventSeries?.ID ?? event.ID,
              },
              platforms: ['ga'],
            });

            setError(error.message);
            reset();
          },
          buttonStyle: {
            height: '48px',
            size: 'large',
            radius: 'round',
            action: 'pay',
          },
        }
      );

      // Store instance for cleanup
      AppleGooglePayInstanceRef.current = paymentRequestInstance;

      const method = await paymentRequestInstance.canMakePayment();
      if (method) {
        paymentRequestInstance.render();
      } else {
        paymentRequestInstance.destroy();
        AppleGooglePayInstanceRef.current = null;
      }
    } catch (error) {
      handleError(error, {
        inAsync: true,
        tags: {
          operation: 'initializeApplePayGooglePay',
          component: 'RevolutCardForm',
        },
      });
      initAppleGooglePayRef.current = false;
    }
  }, [totalAmount, currency, clientSecret]);

  const initializeRevolutPay = useCallback(async () => {
    if (
      !RevolutPayMountRef.current ||
      totalAmount === null ||
      totalAmount === undefined ||
      initRevolutPayRef.current
    ) {
      return;
    }

    // Claim the mount before awaiting, so a concurrent call cannot also mount.
    initRevolutPayRef.current = true;

    // Save state BEFORE RevolutPay button mounts (it will redirect on click)
    // Use direct localStorage writes to avoid React batching + Jotai async persistence issues
    if (clientSecret && cartId && event) {
      try {
        const redirectKey = clientSecret;

        if (localStorage) {
          // Direct localStorage writes with redirect key prefix
          localStorage.setItem(`${redirectKey}-cartId`, cartId);
          localStorage.setItem(`${redirectKey}-event`, JSON.stringify(event));
          localStorage.setItem(
            `${redirectKey}-ticketDetails`,
            JSON.stringify(ticketDetails)
          );
          if (eventSeries) {
            localStorage.setItem(
              `${redirectKey}-eventSeries`,
              JSON.stringify(eventSeries)
            );
          }
          if (promoCode) {
            localStorage.setItem(`${redirectKey}-promoCode`, promoCode);
          }
          if (escrowExpiryTime) {
            localStorage.setItem(
              `${redirectKey}-escrowExpiryTime`,
              escrowExpiryTime.toISOString()
            );
          }
          localStorage.setItem(
            `tabSession-${redirectKey}-createdAt`,
            new Date().toISOString()
          );
        }

        Sentry.addBreadcrumb({
          category: 'RevolutPay',
          message: 'State persisted for RevolutPay with redirect key',
          data: {
            redirectKey,
            cartId,
            eventId: event.ID,
          },
        });
      } catch (error) {
        Sentry.captureException(error, {
          tags: {
            operation: 'persistRedirectState',
            component: 'RevolutCardForm',
          },
          extra: { cartId, eventId: event?.ID },
        });
      }
    }

    try {
      const paymentsInstance = await RevolutCheckout.payments({
        locale,
        publicToken,
        mode: environment,
      });

      // Check if component is still mounted and ref is still valid
      if (!RevolutPayMountRef.current) {
        initRevolutPayRef.current = false;
        return;
      }

      const revolutPay = paymentsInstance.revolutPay;
      const paymentOptions = {
        currency,
        totalAmount,
        createOrder: () =>
          new Promise<{ publicId: string }>(resolve =>
            resolve({
              publicId: clientSecret,
            })
          ),
        buttonStyle: {
          height: '48px',
          radius: 'round',
          size: 'large',
          variant: 'light',
          width: '100%',
          action: 'pay',
        } as const,
        redirectUrls: {
          success: `${FRONTEND_BASE_URL}/event/${event?.ID}/checkout/contact`,
          failure: `${FRONTEND_BASE_URL}/resume-checkout/${event?.ID}/${cartId}`,
          cancel: `${FRONTEND_BASE_URL}/resume-checkout/${event?.ID}/${cartId}`,
        },
        // Mobile-specific redirect URLs - required to allow in-app payment flow
        mobileRedirectUrls: {
          success: `${FRONTEND_BASE_URL}/event/${event?.ID}/checkout/contact`,
          failure: `${FRONTEND_BASE_URL}/resume-checkout/${event?.ID}/${cartId}`,
          cancel: `${FRONTEND_BASE_URL}/resume-checkout/${event?.ID}/${cartId}`,
        },
      };

      revolutPay.mount(RevolutPayMountRef.current, paymentOptions);

      // Store instance for cleanup
      RevolutPayInstanceRef.current = revolutPay;

      revolutPay.on('payment', revolutPaymentEvent => {
        switch (revolutPaymentEvent.type) {
          case 'cancel': {
            // Handle all cancellation scenarios
            Sentry.addBreadcrumb({
              category: 'RevolutPay',
              message: 'Payment cancelled by user',
              level: 'info',
              data: {
                dropOffState: revolutPaymentEvent.dropOffState,
                orderId: revolutPaymentEvent.orderId,
              },
            });

            tracker.track({
              event: {
                event: 'Payment Cancelled',
                category: 'Payment',
                label: `DropOff: ${revolutPaymentEvent.dropOffState}`,
                value: eventSeries?.ID ?? event.ID,
              },
              platforms: ['ga'],
            });

            // Re-enable checkout UI
            setBlocked(false);
            setLoading(false);

            // Optionally show user-friendly message based on dropOffState
            if (revolutPaymentEvent.dropOffState === 'payment_summary') {
              setError('Payment was cancelled. You can try again when ready.');
            }
            break;
          }
          case 'success': {
            markPaymentSubmitted();
            trackSuccessfulPurchase();
            router.push(
              `/event/${event.ID || eventSeries?.ID}/checkout/contact`
            );
            break;
          }
          case 'error': {
            tracker.track({
              event: {
                event: 'Payment Failed',
                category: 'Payment',
                label: `Amount: ${currencyLookup(currency)}${formatValue(
                  totalAmount
                )}`,
                value: eventSeries?.ID ?? event.ID,
              },
              platforms: ['ga'],
            });
            setError(revolutPaymentEvent.error.message);
            reset();
            break;
          }
        }
      });
    } catch (error) {
      handleError(error, {
        inAsync: true,
        tags: {
          operation: 'initializeRevolutPay',
          component: 'RevolutCardForm',
        },
      });
      initRevolutPayRef.current = false;
      setError(
        'Failed to load Revolut Pay. Please try another payment method.'
      );
    }
  }, [
    totalAmount,
    currency,
    clientSecret,
    cartId,
    event,
    ticketDetails,
    eventSeries,
    promoCode,
    escrowExpiryTime,
  ]);

  const initializeRevolutCardField = useCallback(async () => {
    if (!cardFieldMountRef.current || initRevolutCardFieldRef.current) {
      return;
    }

    // Claim the mount before awaiting, so a concurrent call cannot also mount.
    initRevolutCardFieldRef.current = true;

    try {
      const instance = await RevolutCheckout(clientSecret, environment);

      // Check if component is still mounted and ref is still valid
      if (!cardFieldMountRef.current) {
        initRevolutCardFieldRef.current = false;
        return;
      }

      cardFieldInstanceRef.current = instance.createCardField({
        target: cardFieldMountRef.current as HTMLElement,
        hidePostcodeField: false,
        onStatusChange: ({ invalid, empty, completed }) =>
          setIsCardValid(!invalid && !empty && completed),
        onSuccess: () => {
          markPaymentSubmitted();
          trackSuccessfulPurchase();
          router.push(`/event/${event.ID || eventSeries?.ID}/checkout/contact`);
        },
        onError: error => {
          setError(error.message);
          reset();
          tracker.track({
            event: {
              event: 'Payment Failed',
              category: 'Payment',
              label: `Amount: ${currencyLookup(currency)}${formatValue(
                totalAmount
              )}`,
              value: eventSeries?.ID ?? event.ID,
            },
            platforms: ['ga'],
          });
        },
        showLoadingIndicator: true,
        styles: {
          default: {
            color: '#ffffff',
          },
          autofilled: {
            color: '#ffffff',
          },
        },
      });
    } catch (error) {
      handleError(error, {
        inAsync: true,
        tags: {
          operation: 'initializeRevolutCardField',
          component: 'RevolutCardForm',
        },
      });
      initRevolutCardFieldRef.current = false;
      setError(
        'Failed to load card payment form. Please try again or use another payment method.'
      );
    }
  }, [clientSecret, environment, totalAmount, currency]);

  try {
    const isCartReadyForCheckout = cartData && tickets && tickets[0];
    useEffect(
      withErrorBoundary(() => {
        if (isCartReadyForCheckout) {
          tracker.trackBeginCheckout(
            cartData,
            tickets[0].Currency,
            event,
            promoCode
          );
        }
      }),
      [cartData, tickets]
    );

    const formEntriesValid = useCallback(
      withErrorHandler(() =>
        setBlocked(cardholderName.length < 1 || !isCardValid)
      ),
      [cardholderName, setBlocked, isCardValid]
    );
    useEffect(
      () => formEntriesValid(),
      [isCardValid, cardholderName, formEntriesValid]
    );

    // Initialize payment methods when cart data is ready. Each initializer
    // guards itself with a ref, so a re-run here is a no-op rather than a
    // second mount.
    useEffect(() => {
      if (!cartData || !totalAmount) {
        return;
      }

      Promise.all([
        initializeApplePayGooglePay(),
        initializeRevolutPay(),
        initializeRevolutCardField(),
      ]).finally(() => setIsInitializing(false));
    }, [
      cartData,
      totalAmount,
      initializeApplePayGooglePay,
      initializeRevolutPay,
      initializeRevolutCardField,
    ]);

    /**
     * Destroy the SDK instances on unmount only. This cannot live on the
     * initialization effect: that effect re-runs whenever an initializer's
     * identity changes, and tearing the buttons down there leaves the mount
     * guards claimed, so nothing rebuilds them.
     */
    useEffect(() => {
      return () => {
        if (cardFieldInstanceRef.current) {
          try {
            cardFieldInstanceRef.current.destroy();
          } catch (e) {
            handleError(e, {
              inAsync: true,
              tags: {
                operation: 'cleanupOnUnmount',
                component: 'RevolutCardForm',
                instance: 'cardField',
              },
            });
          }
        }
        if (AppleGooglePayInstanceRef.current) {
          try {
            AppleGooglePayInstanceRef.current.destroy();
          } catch (e) {
            handleError(e, {
              inAsync: true,
              tags: {
                operation: 'cleanupOnUnmount',
                component: 'RevolutCardForm',
                instance: 'appleGooglePay',
              },
            });
          }
        }
        if (RevolutPayInstanceRef.current) {
          try {
            RevolutPayInstanceRef.current.destroy();
          } catch (e) {
            handleError(e, {
              inAsync: true,
              tags: {
                operation: 'cleanupOnUnmount',
                component: 'RevolutCardForm',
                instance: 'revolutPay',
              },
            });
          }
        }
      };
    }, []);

    const containerStyle = {
      display: 'flex',
      gap: '4px',
    };

    return (
      <div>
        {/* Loading spinner during initialization */}
        {isInitializing && (
          <div
            style={{
              textAlign: 'center',
              padding: '20px',
              color: 'rgba(var(--primary-colour), 1)',
            }}
          >
            <div style={{ marginBottom: '10px' }}>
              Loading payment options...
            </div>
            <div className='spinner' />
          </div>
        )}

        {/* Error display */}
        {error && (
          <div
            style={{
              marginBottom: '15px',
              padding: '12px',
              backgroundColor: 'rgba(255, 0, 0, 0.1)',
              borderLeft: '4px solid rgba(255, 0, 0, 0.6)',
              borderRadius: '4px',
            }}
            className='error-message'
          >
            {error}
            {didFail && (
              <>
                <br />
                <span>You'll need to reserve a new ticket...</span>
              </>
            )}
            {!didFail && (
              <button
                onClick={() => setError(null)}
                style={{
                  marginLeft: '10px',
                  padding: '4px 8px',
                  background: 'transparent',
                  border: '1px solid currentColor',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '12px',
                }}
              >
                Dismiss
              </button>
            )}
          </div>
        )}

        <div className='DigitalPaymentsWrapper' style={containerStyle}>
          <div
            id='apple-pay-google-pay'
            className='payment-button'
            ref={AppleGooglePayMountRef}
          />
          <div
            id='revolut-pay'
            className='payment-button'
            ref={RevolutPayMountRef}
          />
        </div>
        <OrSeparator />
        <form
          id='formBilling'
          style={{ padding: '15px 2px 10px', height: '80%' }}
        >
          <div className='form-row'>
            <InputField
              autoComplete='name'
              type='text'
              id='cardholderName'
              label='Cardholder Name'
              value={cardholderName}
              onChange={uiEvent => {
                if (error) setError(null);
                const target = uiEvent.target as HTMLTextAreaElement;
                setCardholderName(target.value ?? '');
              }}
              required
            />
          </div>
          <div className='form-row'>
            <div
              id='revolut-pay-form'
              ref={cardFieldMountRef}
              className='form-field'
              style={{
                minHeight: '69.5px',
                padding: '16px',
              }}
            ></div>
          </div>
          <CtaContainer
            buttonText={`Pay Now (${currencyLookup(currency)}${formatValue(
              totalAmount
            )})`}
            blocked={blocked}
            loading={loading}
            onButtonClick={withErrorHandler(handleSubmit)}
          />
        </form>
      </div>
    );
  } catch (error) {
    return (
      <div className='error-message'>
        An error occurred while loading the payment form. Please try again
        later.
      </div>
    );
  }
}

function OrSeparator() {
  return (
    <div className='OrSeparatorWrapper' style={separatorStyle}>
      <div style={lineStyle} />
      <span className='OrSeparatorText'>or</span>
      <div style={lineStyle} />
    </div>
  );
}

export default RevolutCardForm;
